// Browser Support Checks (Feature Detection)
// Prüft, ob der Browser bestimmte Features unterstützt, bevor wir sie verwenden

/**
 * Check if browser supports requestIdleCallback
 * Used for non-blocking background tasks
 */
export function supportsRequestIdleCallback(): boolean {
  return typeof window !== 'undefined' && 'requestIdleCallback' in window;
}

/**
 * Check if browser supports View Transitions API
 * Used for smooth page transitions (Chrome 111+, Edge 111+)
 */
export function supportsViewTransitions(): boolean {
  return typeof document !== 'undefined' && 'startViewTransition' in document;
}

/**
 * Check if browser supports Intersection Observer
 * Used for lazy loading and scroll-based animations
 */
export function supportsIntersectionObserver(): boolean {
  return typeof window !== 'undefined' && 'IntersectionObserver' in window;
}

/**
 * Check if browser supports ResizeObserver
 * Used for responsive components
 */
export function supportsResizeObserver(): boolean {
  return typeof window !== 'undefined' && 'ResizeObserver' in window;
}

/**
 * Check if browser supports Web Audio API
 * Used for audio processing
 */
export function supportsWebAudio(): boolean {
  return typeof window !== 'undefined' && 
    ('AudioContext' in window || 'webkitAudioContext' in window);
}

/**
 * Check if browser supports Service Workers
 * Used for offline functionality and caching
 */
export function supportsServiceWorkers(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

/**
 * Check if browser supports IndexedDB
 * Used for local storage of large data
 */
export function supportsIndexedDB(): boolean {
  return typeof window !== 'undefined' && 'indexedDB' in window;
}

/**
 * Check if browser supports WebP image format
 */
export function supportsWebP(): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof Image === 'undefined') {
      resolve(false);
      return;
    }
    
    const webP = new Image();
    webP.onload = webP.onerror = () => {
      resolve(webP.height === 2);
    };
    webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
  });
}

/**
 * Get browser name and version
 */
export function getBrowserInfo(): { name: string; version: string } {
  if (typeof navigator === 'undefined') {
    return { name: 'Unknown', version: 'Unknown' };
  }

  const ua = navigator.userAgent;
  let name = 'Unknown';
  let version = 'Unknown';

  // Chrome
  if (ua.includes('Chrome') && !ua.includes('Edg')) {
    const match = ua.match(/Chrome\/(\d+)/);
    name = 'Chrome';
    version = match ? match[1] : 'Unknown';
  }
  // Firefox
  else if (ua.includes('Firefox')) {
    const match = ua.match(/Firefox\/(\d+)/);
    name = 'Firefox';
    version = match ? match[1] : 'Unknown';
  }
  // Safari
  else if (ua.includes('Safari') && !ua.includes('Chrome')) {
    const match = ua.match(/Version\/(\d+)/);
    name = 'Safari';
    version = match ? match[1] : 'Unknown';
  }
  // Edge
  else if (ua.includes('Edg')) {
    const match = ua.match(/Edg\/(\d+)/);
    name = 'Edge';
    version = match ? match[1] : 'Unknown';
  }

  return { name, version };
}

/**
 * Check if browser is modern (supports ES6+ and modern APIs)
 */
export function isModernBrowser(): boolean {
  return (
    supportsRequestIdleCallback() &&
    supportsIntersectionObserver() &&
    supportsResizeObserver() &&
    supportsWebAudio()
  );
}

